/**
 * Agent tools over the service-mapping store.
 *
 * Schema convention matches the Zendesk and KB CMS tools: required fields are
 * bare, everything optional is `.nullable()` with a `.describe()` saying what
 * null means. Never `.optional()` or `.default()` — the provider's strict
 * function schema requires every property in `required`, and `.optional()`
 * marks a field as not-required, which makes the whole request 400.
 *
 * All mutations go through services/store.ts, i.e. localStorage only.
 */

import { tool } from "ai"
import { z } from "zod"
import { withQueue } from "@/pages/dev/smart-cms/shared/toolQueue"
import { describeLanguage, nativeLanguageName, normalizeLocale } from "@/pages/dev/smart-cms/shared/languages"
import { SITE_LOCALE_CODES } from "@/pages/dev/smart-cms/shared/siteLanguages"
import * as db from "@/pages/dev/smart-cms/service-map/store"
import {
  type ParsedSheet, type ColumnMapping, type MappedField, type ImportPlan,
  guessMapping, planImport, applyImport,
} from "@/pages/dev/smart-cms/service-map/import"
import {
  type ServiceStore, type EntityKind, displayName, BUCKET, localesByStatus, serviceStatusFor,
  CONTACT_CHANNELS, HOUR_DAYS, SERVICE_STATUSES, PROVIDER_STATUSES,
  CATEGORY_ICONS, CATEGORY_COLORS,
  normalizeHourDay, normalizeTime,
} from "@/pages/dev/smart-cms/service-map/types"
import type { ChangeEntry, ChangeAction, EntityVersion } from "@/pages/dev/smart-cms/service-map/history"

export interface ServiceToolContext {
  getStore: () => ServiceStore
  setStore: (next: ServiceStore) => void
  locale: string
  onEntityFocused?: (kind: EntityKind, id: string) => void
  /** Called after a successful delete, so the caller can clear selection/editor state. */
  onEntityDeleted?: (kind: EntityKind, id: string) => void
  onChanged?: () => void
  /** The spreadsheet the user uploaded, if any. */
  getSheet?: () => ParsedSheet | null
  /** HITL gate for destructive operations. */
  onRequestApproval?: (req: { summary: string; detail?: string }) => Promise<boolean>
  /**
   * Import-specific approval: shown a full ImportPlan (counts, new values with
   * typo hints, row issues) rather than the generic summary/detail pair, so the
   * UI can offer a per-value approve/reject rather than one yes/no on the
   * whole batch. Resolves to null on rejection, or the set of NewValue.key
   * strings the user actually approved.
   */
  onRequestImportApproval?: (args: {
    sheetName: string
    rowCount: number
    plan: ImportPlan
    limit?: number
  }) => Promise<string[] | null>
  autoAccept?: boolean
  /** Records a change so it appears in the audit log and can be reverted. */
  record?: (args: {
    prevStore: ServiceStore
    nextStore?: ServiceStore
    action: ChangeAction
    actor: "agent" | "user"
    entityKind: EntityKind
    entityId: string
    summary: string
    locale?: string
  }) => void
  /**
   * Records many changes with one history flush. Used by the batch tools, where
   * per-entry record() calls would re-serialize the whole history each time.
   * Callers supply `before` explicitly, since a batch shares one mutable store.
   */
  recordMany?: (entries: Array<{
    action: ChangeAction
    actor: "agent" | "user"
    entityKind: EntityKind
    entityId: string
    entityName: string
    summary: string
    locale?: string
    before?: Record<string, any>
    after?: Record<string, any>
  }>) => void
  /**
   * Logs a bulk operation (a CSV/Excel import) as one line, not attached to a
   * single entity. Not individually restorable — see history.ts's makeBulkEntry.
   */
  recordBulk?: (summary: string) => void
  /** Reads the change log, newest last. */
  getHistory?: () => ChangeEntry[]
  /** Reverts one logged change by id. */
  revert?: (entryId: string) => { ok: true; description: string } | { ok: false; error: string }
  /** Version timeline for one entity, newest first. */
  versionsOf?: (entityId: string) => EntityVersion[]
  /** Restores an entity to a previously-recorded version. */
  restoreEntityVersion?: (entityId: string, entryId: string, kind: EntityKind) =>
    { ok: true; description: string } | { ok: false; error: string }
}

const ok = (data: any) => data
const fail = (message: string) => ({ error: message })

function tryDo<T>(fn: () => T): T | { error: string } {
  try { return fn() } catch (e: any) { return fail(e?.message || String(e)) }
}

// Derived from BUCKET so a new entity kind can't be added to the model without
// showing up in the tool schemas.
const ENTITY_KINDS = Object.keys(BUCKET) as [EntityKind, ...EntityKind[]]

/**
 * Writable columns, declared explicitly.
 *
 * A free-form record type would serialize to an object schema with no
 * `properties`, which strict function-calling rejects. Every field is nullable
 * (never `.optional()`) so it can be sent as null and skipped, per the
 * convention at the top of this file.
 */
const ENTITY_FIELDS = {
  name: z.string().nullable().describe("Name, also written as the active locale's translation"),
  description: z.string().nullable().describe("HTML description (services and providers)"),
  address: z.string().nullable().describe("Street address (services)"),
  definition: z.string().nullable().describe("Markdown definition (categories, populations, accessibility)"),

  status: z.string().nullable().describe("Service status, or provider Active/Not Active — see svc_schema_reference"),
  countryId: z.string().nullable().describe("Owning country id"),
  regionId: z.string().nullable().describe("Owning region id"),
  cityId: z.string().nullable().describe("Owning city id"),
  providerId: z.string().nullable().describe("Owning provider id (services)"),
  categoryId: z.string().nullable().describe("Provider category id (providers)"),
  categoryIds: z.array(z.string()).nullable().describe("Service category ids"),
  tagIds: z.array(z.string()).nullable().describe("Tag ids"),
  populationIds: z.array(z.string()).nullable().describe("Population ids served"),
  accessibilityIds: z.array(z.string()).nullable().describe("Accessibility option ids"),

  physicalLocation: z.boolean().nullable().describe("Whether the service has a physical location"),
  lat: z.number().nullable().describe("Latitude"),
  lon: z.number().nullable().describe("Longitude"),
  alwaysOpen: z.boolean().nullable().describe("Open at all times; hides the hours list"),
  hours: z.array(z.object({
    // Enumerated rather than free text so an invalid day cannot be sent at all.
    // Note the trailing space in "Monday to Friday " — it is the stored value.
    day: z.enum([
      "Monday to Friday ", "Sunday", "Monday", "Tuesday", "Wednesday",
      "Thursday", "Friday", "Saturday",
    ]).describe("Day or weekday block. Use 'Monday to Friday ' (with the trailing space) for weekdays"),
    open: z.string().describe("Opening time, 24-hour \"HH:mm\", e.g. \"09:00\""),
    close: z.string().describe("Closing time, 24-hour \"HH:mm\", e.g. \"17:00\""),
  })).nullable().describe("Opening hours; replaces the whole list"),
  contactInfo: z.array(z.object({
    channel: z.string().describe("A value from CONTACT_CHANNELS — see svc_schema_reference"),
    contactDetails: z.string().describe("The number, address or link"),
  })).nullable().describe("Public contact channels; replaces the whole list"),

  contactName: z.string().nullable().describe("Administrative contact first name"),
  contactLastName: z.string().nullable().describe("Administrative contact last name"),
  contactTitle: z.string().nullable().describe("Administrative contact job title"),
  contactEmail: z.string().nullable().describe("Administrative contact email"),
  contactPhone: z.string().nullable().describe("Administrative contact phone"),

  headerImage: z.string().nullable().describe("Header image URL. You cannot upload files — if the user wants a new image, tell them to use the Upload button on this field in the editor, then reference the URL it produces."),
  source: z.string().nullable().describe("Provenance, e.g. Manual"),
  site: z.string().nullable().describe("Site slug (countries)"),
  code: z.string().nullable().describe("Locale code (languages only) — the primary key"),
  direction: z.enum(["ltr", "rtl"]).nullable().describe("Text direction (languages only)"),
  expiration: z.string().nullable().describe("Review date, yyyy-mm-dd (providers)"),

  // Enums so the agent cannot send a value the public map will not draw
  icon: z.enum(CATEGORY_ICONS).nullable().describe(
    "Map pin icon (service categories). Must be one of the listed Material Symbol names — any other value leaves the pin blank on the public site."
  ),
  color: z.enum(CATEGORY_COLORS).nullable().describe(
    "Category colour as a hex string from the fixed palette (service categories) — see svc_schema_reference"
  ),
}

/** Drops the nulls, which mean "not supplied" rather than "set to null". */
function usedFields(fields: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(fields)) if (v !== null && v !== undefined) out[k] = v
  return out
}

/**
 * Normalises `fields.hours` in place, returning an error string when a row is
 * unusable. Times are accepted in several forms and rewritten to "HH:mm";
 * days must resolve to a HOUR_DAYS value or the write is rejected.
 */
function normalizeHours(fields: Record<string, any>): string | null {
  if (!Array.isArray(fields.hours)) return null

  const out: any[] = []
  for (const [i, entry] of fields.hours.entries()) {
    const day = normalizeHourDay(entry?.day ?? "")
    if (!day) {
      return `Opening hours entry ${i + 1}: "${entry?.day}" is not a valid day. ` +
        `Use one of: ${HOUR_DAYS.map(d => `"${d}"`).join(", ")}.`
    }
    const open = normalizeTime(entry?.open ?? "")
    const close = normalizeTime(entry?.close ?? "")
    if (!open || !close) {
      return `Opening hours entry ${i + 1} (${day.trim()}): ` +
        `could not read "${entry?.open}"–"${entry?.close}" as times. Use 24-hour "HH:mm", e.g. "09:00".`
    }
    out.push({ day, open, close })
  }
  fields.hours = out
  return null
}

/** Coerces the model's loose mapping object into a ColumnMapping. */
function toMapping(raw: {
  columns?: Array<{ header?: string; field?: string }>
  multiValueSeparator?: string | null
  locale?: string | null
  idColumn?: string | null
}): ColumnMapping {
  const columns: Record<string, MappedField> = {}
  for (const { header, field } of raw.columns ?? []) {
    if (!header || !field) continue
    columns[header] = field as MappedField
  }
  return {
    columns,
    multiValueSeparator: raw.multiValueSeparator ?? ";",
    locale: raw.locale ?? undefined,
    idColumn: raw.idColumn ?? undefined,
  }
}

export function makeServiceTools(ctx: ServiceToolContext, hitlQueue?: { current: Promise<void> }) {
  const L = () => ctx.locale
  const commit = (next: ServiceStore) => { ctx.setStore(next); ctx.onChanged?.() }

  /**
   * Commits a change AND records it in the audit log, so every agent mutation
   * is traceable and revertable — the UI's own create/update/delete already
   * do this (see useServiceCMS.ts); tool calls go straight through
   * ctx.setStore and would otherwise leave no trail at all.
   *
   * Deletes must pass `prevStore` as the store BEFORE deletion, since the
   * snapshot has to be taken while the entity still exists.
   */
  const commitTracked = (args: {
    prevStore: ServiceStore
    nextStore: ServiceStore
    action: ChangeAction
    entityKind: EntityKind
    entityId: string
    summary: string
    locale?: string
  }) => {
    commit(args.nextStore)
    ctx.record?.({
      prevStore: args.prevStore, nextStore: args.nextStore, action: args.action,
      actor: "agent", entityKind: args.entityKind, entityId: args.entityId,
      summary: args.summary, locale: args.locale,
    })
  }

  /** Same as commitTracked, but for a delete — no nextStore, since the entity is gone. */
  const commitTrackedDelete = (args: {
    prevStore: ServiceStore
    nextStore: ServiceStore
    entityKind: EntityKind
    entityId: string
    summary: string
    locale?: string
  }) => {
    // Record first: the snapshot needs the pre-delete store.
    ctx.record?.({
      prevStore: args.prevStore, action: "delete",
      actor: "agent", entityKind: args.entityKind, entityId: args.entityId,
      summary: args.summary, locale: args.locale,
    })
    commit(args.nextStore)
  }

  const gate = (summary: string, detail?: string): Promise<boolean> => {
    if (ctx.autoAccept) return Promise.resolve(true)
    if (!ctx.onRequestApproval) return Promise.resolve(true)
    const ask = () => ctx.onRequestApproval!({ summary, detail })
    // Serialize approvals: the model often emits several destructive calls in
    // one step and the SDK runs them concurrently, so without a queue the later
    // prompts clobber the earlier ones and those calls never settle.
    //
    // withQueue (shared with the CMS backend) is used rather than an inline
    // chain because it also CATCHES a rejected approval and rethrows it to this
    // caller. An inline `try/finally` leaves the rejection on hitlQueue.current,
    // poisoning the shared chain so every later approval in the session rejects
    // too.
    return hitlQueue ? withQueue(hitlQueue, ask) : ask()
  }

  const statusFor = (svc: any, locale: string) => serviceStatusFor(svc, locale)

  const name = (kind: EntityKind, id: string) => {
    const s = ctx.getStore()
    const e = db.getEntity(s, kind, id)
    return e ? displayName(e, L(), s.defaultLocale) : id
  }

  return {
    // ── Read ────────────────────────────────────────────────────────────────
    svc_overview: tool({
      description:
        "Overview of the service-mapping database: entity counts, configured languages, and the country list. " +
        "Call this FIRST to orient yourself before making changes.",
      parameters: z.object({}),
      execute: async () => tryDo(() => {
        const s = ctx.getStore()
        return ok({
          defaultLocale: s.defaultLocale,
          languages: Object.values(s.languages).map(l => ({ code: l.code, name: l.name, direction: l.direction })),
          stats: db.stats(s),
          countries: Object.values(s.countries).map(c => ({
            id: c.id,
            name: displayName(c, L(), s.defaultLocale),
            site: c.site,
            regions: db.regionsOfCountry(s, c.id).length,
            services: Object.values(s.services).filter(x => x.countryId === c.id).length,
          })),
        })
      }),
    }),

    svc_search: tool({
      description:
        "Ranked keyword search across services, providers, categories, tags, populations, accessibility options, " +
        "and geography. Name matches outrank description matches; whole phrases rank highest; partial words work (3+ chars). " +
        "Use the filters to narrow by entity kind, locale, service status, geography, provider, or category. " +
        "Prefer this over listing everything when looking for something specific.",
      parameters: z.object({
        query: z.string().describe("Words to look for"),
        kinds: z.array(z.enum(ENTITY_KINDS)).nullable().describe("Restrict to these entity kinds, or null for the usual set"),
        locale: z.string().nullable().describe("Only this locale, or null to search all"),
        status: z.enum(["draft", "ready for translation", "Translated", "published", "archived", "private"])
          .nullable().describe("Services in this status only, or null for any"),
        countryId: z.string().nullable().describe("Services in this country only, or null"),
        regionId: z.string().nullable().describe("Services in this region only, or null"),
        cityId: z.string().nullable().describe("Services in this city only, or null"),
        providerId: z.string().nullable().describe("Services from this provider only, or null"),
        categoryIds: z.array(z.string()).nullable().describe("Services carrying ALL these category ids, or null"),
        limit: z.number().nullable().describe("Max results (default 25)"),
      }),
      execute: async (a) => tryDo(() => {
        const s = ctx.getStore()
        const results = db.searchStore(s, a.query, {
          limit: a.limit ?? 25,
          kinds: a.kinds ?? undefined,
          locale: a.locale ?? undefined,
          status: a.status ?? undefined,
          countryId: a.countryId ?? undefined,
          regionId: a.regionId ?? undefined,
          cityId: a.cityId ?? undefined,
          providerId: a.providerId ?? undefined,
          categoryIds: a.categoryIds ?? undefined,
        })
        return ok({ query: a.query, resultCount: results.length, results })
      }),
    }),

    svc_list: tool({
      description:
        "List every entity of one kind, with ids, names and timestamps. Sort by name or by when records were " +
        "created/updated, and filter to those changed since a date — use this for questions like " +
        "\"what changed recently\" or \"which records are stalest\". Also the way to look up taxonomy and geography — " +
        "categories, tags, populations, accessibility options, countries, regions, cities, providers, languages.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        countryId: z.string().nullable().describe("For region/city/provider: only those in this country, or null for all"),
        sort: z.enum(["name", "recently-updated", "least-recently-updated", "newest", "oldest"])
          .nullable().describe("Order of results, or null for name"),
        updatedSince: z.string().nullable()
          .describe("Only records changed on or after this ISO date (e.g. '2026-08-01'), or null for any"),
        limit: z.number().nullable().describe("Max rows (default 200)"),
      }),
      execute: async ({ kind, countryId, sort, updatedSince, limit }) => tryDo(() => {
        const s = ctx.getStore()
        let rows = db.listEntities(s, kind)
        if (countryId) rows = rows.filter((r: any) => r.countryId === countryId)

        if (updatedSince) {
          const cutoff = Date.parse(updatedSince)
          if (Number.isNaN(cutoff)) return fail(`Could not read "${updatedSince}" as a date. Use ISO form, e.g. 2026-08-01.`)
          rows = rows.filter((r: any) => (r.updatedAt ?? 0) >= cutoff)
        }

        const byName = (a: any, b: any) =>
          displayName(a, L(), s.defaultLocale).localeCompare(displayName(b, L(), s.defaultLocale))
        rows = [...rows].sort(
          sort === "recently-updated" ? (a: any, b: any) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0)
          : sort === "least-recently-updated" ? (a: any, b: any) => (a.updatedAt ?? 0) - (b.updatedAt ?? 0)
          : sort === "newest" ? (a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0)
          : sort === "oldest" ? (a: any, b: any) => (a.createdAt ?? 0) - (b.createdAt ?? 0)
          : byName,
        )

        return ok({
          kind,
          total: rows.length,
          sortedBy: sort ?? "name",
          items: rows.slice(0, limit ?? 200).map((r: any) => ({
            id: r.id ?? r.code,
            name: displayName(r, L(), s.defaultLocale),
            locales: Object.keys(r.translations ?? {}),
            // ISO so the model can compare and report dates without guessing.
            updatedAt: r.updatedAt ? new Date(r.updatedAt).toISOString() : null,
            createdAt: r.createdAt ? new Date(r.createdAt).toISOString() : null,
            ...(kind === "service"
              ? { status: statusFor(r, L()), path: db.servicePath(s, r.id, L()) }
              : {}),
            ...(kind === "provider" ? { status: r.status, countryId: r.countryId } : {}),
          })),
        })
      }),
    }),

    svc_get: tool({
      description:
        "Read one entity in full, with every field, its relations resolved to names, and all locales. " +
        "Use this before updating anything so you know the current values.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        id: z.string(),
      }),
      execute: async ({ kind, id }) => tryDo(() => {
        const s = ctx.getStore()
        const e = db.getEntity(s, kind, id)
        if (!e) return fail(`No such ${kind}: ${id}`)
        ctx.onEntityFocused?.(kind, id)

        const resolved: Record<string, any> = {}
        if (kind === "service") {
          resolved.country = e.countryId ? name("country", e.countryId) : null
          resolved.region = e.regionId ? name("region", e.regionId) : null
          resolved.city = e.cityId ? name("city", e.cityId) : null
          resolved.provider = e.providerId ? name("provider", e.providerId) : null
          resolved.categories = (e.categoryIds ?? []).map((x: string) => name("serviceCategory", x))
          resolved.tags = (e.tagIds ?? []).map((x: string) => name("tag", x))
          resolved.populations = (e.populationIds ?? []).map((x: string) => name("population", x))
          resolved.accessibility = (e.accessibilityIds ?? []).map((x: string) => name("accessibility", x))
          resolved.path = db.servicePath(s, id, L())
          resolved.statusByLocale = localesByStatus(e)
        }
        if (kind === "provider") {
          resolved.country = e.countryId ? name("country", e.countryId) : null
          resolved.category = e.categoryId ? name("providerCategory", e.categoryId) : null
          resolved.serviceCount = db.servicesOfProvider(s, id).length
        }

        return ok({
          kind,
          entity: e,
          resolved: {
            ...resolved,
            updatedAt: e.updatedAt ? new Date(e.updatedAt).toISOString() : null,
            createdAt: e.createdAt ? new Date(e.createdAt).toISOString() : null,
          },
        })
      }),
    }),

    // ── Create ──────────────────────────────────────────────────────────────
    svc_create: tool({
      description:
        "Create an entity. `fields` carries the entity's own columns — see svc_get on an existing record of the same kind " +
        "for the exact shape. A `name` in fields also seeds the translation for the active locale. " +
        "Geography rules from the source schema: a region needs countryId; a city needs BOTH countryId and regionId; " +
        "a provider needs countryId. Tags are a flat vocabulary with no parent category.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        fields: z.object(ENTITY_FIELDS).describe("Column values for the new entity. Send null for anything not applicable."),
        locale: z.string().nullable().describe("Locale to write the name/description into, or null for the active locale"),
      }),
      execute: async ({ kind, fields: rawFields, locale }) => tryDo(() => {
        const fields = usedFields(rawFields)
        if (kind === "language") {
          const code = fields.code
          if (!code) return fail("A language needs a `code` (e.g. 'es-MX') — it is the primary key.")
          const next = db.upsertLanguage(ctx.getStore(), {
            // `transifex` (the Directus locale-tag list) is deliberately not
            // exposed on the tool schema — it would cost a property on every
            // svc_create/svc_update call to serve one rarely-used field on one
            // entity kind. Set it from the UI instead.
            code, name: fields.name ?? code, direction: fields.direction ?? "ltr",
          })
          commit(next)
          return ok({ kind, id: code, name: fields.name ?? code })
        }

        // Validate the required FKs the source schema enforces, so the local
        // data can't drift into shapes Directus would reject.
        const s0 = ctx.getStore()
        if (kind === "region" && !fields.countryId) return fail("A region requires countryId.")
        if (kind === "city" && (!fields.countryId || !fields.regionId)) return fail("A city requires both countryId and regionId.")
        if (kind === "provider" && !fields.countryId) return fail("A provider requires countryId.")
        for (const [f, k] of [["countryId", "country"], ["regionId", "region"], ["cityId", "city"], ["providerId", "provider"]] as const) {
          if (fields[f] && !db.getEntity(s0, k as EntityKind, fields[f])) return fail(`No such ${k}: ${fields[f]}`)
        }

        const hoursCheck = normalizeHours(fields)
        if (hoursCheck) return fail(hoursCheck)

        const { store, id } = db.createEntity(s0, kind, fields, { locale: locale || L() })
        commitTracked({
          prevStore: s0, nextStore: store, action: "create",
          entityKind: kind, entityId: id, locale: locale || L(),
          summary: `Created ${kind} "${fields.name ?? id}"`,
        })
        ctx.onEntityFocused?.(kind, id)
        return ok({ kind, id, name: fields.name })
      }),
    }),

    // ── Update ──────────────────────────────────────────────────────────────
    svc_update: tool({
      description:
        "Update an entity's own columns. Pass only the fields you want to change; omitted fields are left alone. " +
        "This does NOT touch translations — use svc_set_translation for localized name/description/address.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        id: z.string(),
        fields: z.object(ENTITY_FIELDS).describe("Columns to change. Send null for anything you are not changing."),
      }),
      execute: async ({ kind, id, fields: rawFields }) => {
        const fields = usedFields(rawFields)
        if (!Object.keys(fields).length) return fail("Nothing to change — supply at least one non-null field.")
        const s0 = ctx.getStore()
        if (!db.getEntity(s0, kind, id)) return fail(`No such ${kind}: ${id}`)
        const approved = await gate(
          `Update ${kind} "${name(kind, id)}"`,
          Object.entries(fields).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join("\n"),
        )
        if (!approved) return fail("Rejected by reviewer.")
        const hoursCheck = normalizeHours(fields)
        if (hoursCheck) return fail(hoursCheck)

        return tryDo(() => {
          const before = ctx.getStore()
          const after = db.updateEntity(before, kind, id, fields)
          commitTracked({
            prevStore: before, nextStore: after, action: "update",
            entityKind: kind, entityId: id,
            summary: `Updated ${Object.keys(fields).join(", ")} on ${kind}`,
          })
          ctx.onEntityFocused?.(kind, id)
          return ok({ kind, id, updated: Object.keys(fields) })
        })
      },
    }),

    svc_set_translation: tool({
      description:
        "Write one locale's translated values for an entity. Services and providers have name/description; " +
        "services also have a translated address; taxonomy entities have name/definition. " +
        "Preserve HTML structure when translating a description — translate only human-readable text.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        id: z.string(),
        locale: z.string().describe("Target locale, e.g. 'es-MX'"),
        name: z.string().nullable().describe("Translated name, or null to keep current"),
        description: z.string().nullable().describe("Translated HTML description, or null to keep current"),
        address: z.string().nullable().describe("Translated address (services only), or null"),
        definition: z.string().nullable().describe("Translated markdown definition (taxonomy only), or null"),
        reviewed: z.boolean().nullable().describe("Mark this locale human-reviewed, or null to leave the flag alone"),
        status: z.enum(["draft", "ready for translation", "Translated", "published", "archived", "private"])
          .nullable().describe("Publication status for THIS locale, or null to leave it as it is"),
      }),
      execute: async ({ kind, id, locale, ...raw }) => {
        const s0 = ctx.getStore()
        if (!db.getEntity(s0, kind, id)) return fail(`No such ${kind}: ${id}`)

        // Nullable means "leave alone", so strip nulls before writing.
        const patch: Record<string, any> = {}
        for (const [k, v] of Object.entries(raw)) if (v !== null && v !== undefined) patch[k] = v
        if (!Object.keys(patch).length) return fail("Nothing to write — provide at least one field.")

        return tryDo(() => {
          const before = ctx.getStore()
          const after = db.setTranslation(before, kind, id, locale, patch)
          commitTracked({
            prevStore: before, nextStore: after, action: "translate",
            entityKind: kind, entityId: id, locale,
            summary: `Translated ${kind} (${locale})`,
          })
          ctx.onEntityFocused?.(kind, id)
          return ok({ kind, id, locale, written: Object.keys(patch) })
        })
      },
    }),

    svc_set_relations: tool({
      description:
        "Replace a service's many-to-many links: categories, tags, populations, or accessibility options. " +
        "Pass the complete list you want — this replaces rather than appends. Ids are validated first.",
      parameters: z.object({
        serviceId: z.string(),
        categoryIds: z.array(z.string()).nullable().describe("Complete category list, or null to leave unchanged"),
        tagIds: z.array(z.string()).nullable().describe("Complete tag list, or null to leave unchanged"),
        populationIds: z.array(z.string()).nullable().describe("Complete population list, or null to leave unchanged"),
        accessibilityIds: z.array(z.string()).nullable().describe("Complete accessibility list, or null to leave unchanged"),
      }),
      execute: async ({ serviceId, ...rels }) => tryDo(() => {
        const s = ctx.getStore()
        if (!s.services[serviceId]) return fail(`No such service: ${serviceId}`)

        const checks: Array<[string, EntityKind]> = [
          ["categoryIds", "serviceCategory"],
          ["tagIds", "tag"],
          ["populationIds", "population"],
          ["accessibilityIds", "accessibility"],
        ]
        const patch: Record<string, any> = {}
        for (const [field, kind] of checks) {
          const list = (rels as any)[field]
          if (list === null || list === undefined) continue
          for (const id of list) {
            if (!db.getEntity(s, kind, id)) return fail(`No such ${kind}: ${id}`)
          }
          patch[field] = list
        }
        if (!Object.keys(patch).length) return fail("Nothing to change — provide at least one list.")

        const after = db.updateEntity(s, "service", serviceId, patch)
        commitTracked({
          prevStore: s, nextStore: after, action: "update",
          entityKind: "service", entityId: serviceId,
          summary: `Changed ${Object.keys(patch).join(", ")} on service`,
        })
        return ok({ serviceId, updated: Object.keys(patch) })
      }),
    }),

    svc_set_status: tool({
      description:
        "Set a service's publish status. Status is PER LOCALE: pass a locale to publish or hold back just that " +
        "language, or null to set the service-wide default that locales fall back to. " +
        "Never publish a locale whose translation is missing or unreviewed — publish the source language and leave " +
        "the others in draft until a human has checked them. " +
        "Valid values: draft, 'ready for translation', Translated, published, archived, private.",
      parameters: z.object({
        serviceId: z.string(),
        status: z.enum(["draft", "ready for translation", "Translated", "published", "archived", "private"]),
        locale: z.string().nullable().describe("Locale to set, or null for the service-wide default"),
      }),
      execute: async ({ serviceId, status, locale }) => tryDo(() => {
        const s = ctx.getStore()
        const svc = s.services[serviceId]
        if (!svc) return fail(`No such service: ${serviceId}`)

        if (!locale) {
          const after = db.updateEntity(s, "service", serviceId, { status })
          commitTracked({
            prevStore: s, nextStore: after, action: "update",
            entityKind: "service", entityId: serviceId,
            summary: `Set status to ${status} (service-wide)`,
          })
          return ok({ serviceId, status, scope: "service-wide" })
        }

        if (!svc.translations?.[locale]) {
          return fail(
            `No ${locale} translation exists for this service, so there is nothing to publish. ` +
            `Write it with svc_set_translation or svc_translate_batch first.`,
          )
        }
        const after = db.setTranslation(s, "service", serviceId, locale, { status })
        commitTracked({
          prevStore: s, nextStore: after, action: "translate",
          entityKind: "service", entityId: serviceId, locale,
          summary: `Set ${locale} status to ${status}`,
        })
        return ok({ serviceId, status, locale })
      }),
    }),

    // ── Bulk import ───────────────────────────────────────────────────────────────────────────────────────────
    svc_import_inspect: tool({
      description:
        "Inspect an uploaded spreadsheet before importing: returns the column headers, a sample of rows, and a " +
        "SUGGESTED column mapping guessed from the header names. Call this FIRST for any import. " +
        "Read the sample rows carefully and correct the mapping — header names lie, and a column called 'Location' " +
        "may hold a city, a street address or coordinates. Then call svc_import_plan with your corrected mapping.",
      parameters: z.object({
        sampleRows: z.number().nullable().describe("How many rows to show (default 15, max 50)"),
      }),
      execute: async ({ sampleRows }) => tryDo(() => {
        const sheet = ctx.getSheet?.()
        if (!sheet) return fail("No spreadsheet uploaded. Ask the user to upload a CSV or Excel file first.")
        const n = Math.min(sampleRows ?? 15, 50)
        return ok({
          activeSheet: sheet.activeSheet,
          sheetNames: sheet.sheetNames,
          totalRows: sheet.rows.length,
          headers: sheet.headers,
          suggestedMapping: (() => {
            const g = guessMapping(sheet.headers)
            return {
              columns: Object.entries(g.columns).map(([header, field]) => ({ header, field })),
              multiValueSeparator: g.multiValueSeparator,
              idColumn: g.idColumn ?? null,
            }
          })(),
          sampleRows: sheet.rows.slice(0, n),
          fieldOptions: [
            "name", "description", "address", "providerName", "country", "region", "city",
            "categories", "tags", "populations", "accessibility", "lat", "lon", "status",
            "phone", "email", "website", "whatsapp", "externalId", "ignore",
          ],
          note:
            "Map every column you understand and set the rest to 'ignore'. `name` is required — rows without it " +
            "cannot be imported. Multi-value cells (categories, tags) are split on multiValueSeparator.",
        })
      }),
    }),

    svc_import_plan: tool({
      description:
        "Dry run: works out exactly what an import would do WITHOUT changing anything. Returns how many services " +
        "would be created versus updated, every new category/tag/city/provider that would have to be created, and " +
        "any problem rows. Values that closely resemble something existing are flagged as possible typos. " +
        "Always run this and show the user the new values before importing — a typo in one cell otherwise adds a " +
        "category that then looks legitimate.",
      parameters: z.object({
        mapping: z.object({
          columns: z.array(z.object({
            header: z.string().describe("The column header exactly as it appears in the sheet"),
            field: z.enum([
              "name", "description", "address", "providerName", "country", "region", "city",
              "categories", "tags", "populations", "accessibility", "lat", "lon", "status",
              "phone", "email", "website", "whatsapp", "externalId", "ignore",
            ]).describe("Field this column fills, or 'ignore'"),
          })).describe("One entry per column you want to map"),
          multiValueSeparator: z.string().nullable().describe("Separator inside multi-value cells (default ';')"),
          locale: z.string().nullable().describe("Locale the sheet text is written in, or null for the default"),
          idColumn: z.string().nullable().describe("Column holding a stable id for matching, or null"),
        }),
      }),
      execute: async ({ mapping }) => tryDo(() => {
        const sheet = ctx.getSheet?.()
        if (!sheet) return fail("No spreadsheet uploaded.")
        const plan = planImport(ctx.getStore(), sheet, toMapping(mapping))
        return ok({
          ...plan,
          // Issue lists get long on a messy sheet; the counts carry the signal.
          issues: plan.issues.slice(0, 30),
          issueCount: plan.issues.length,
          note:
            plan.newValues.length
              ? "Show the user newValues (especially any with similarTo set — those are likely typos), then pass " +
                "the approved keys to svc_import_apply."
              : "Nothing new would be created; safe to apply.",
        })
      }),
    }),

    svc_import_apply: tool({
      description:
        "Run the import. Applies the mapping to EVERY row in code, so all rows are handled identically. " +
        "Services already present are updated rather than duplicated (matched on the id column, or on name plus " +
        "provider). Only values whose keys appear in approvedKeys get created; anything else is left empty on the " +
        "row and reported. Run svc_import_plan first and get the user's approval.",
      parameters: z.object({
        mapping: z.object({
          columns: z.array(z.object({
            header: z.string(),
            field: z.enum([
              "name", "description", "address", "providerName", "country", "region", "city",
              "categories", "tags", "populations", "accessibility", "lat", "lon", "status",
              "phone", "email", "website", "whatsapp", "externalId", "ignore",
            ]),
          })),
          multiValueSeparator: z.string().nullable(),
          locale: z.string().nullable(),
          idColumn: z.string().nullable(),
        }),
        approvedKeys: z.array(z.string())
          .describe("The `key` of each new value the user approved, from svc_import_plan's newValues"),
        limit: z.number().nullable().describe("Import only the first N rows, to trial a mapping. Null for all."),
      }),
      execute: async ({ mapping, approvedKeys, limit }) => {
        const sheet = ctx.getSheet?.()
        if (!sheet) return fail("No spreadsheet uploaded.")

        const m = toMapping(mapping)
        // Plan the SAME rows the apply will touch, so the review card's
        // create/update/skip counts describe the trial run it announces
        // rather than the whole sheet.
        const plan = planImport(ctx.getStore(), sheet, m, { limit: limit ?? undefined })

        // Prefer the structured import review, which lets the user pick new
        // values one at a time rather than a single yes/no on everything the
        // model already decided to approve.
        let finalApprovedKeys: string[]
        if (ctx.onRequestImportApproval) {
          const result = await ctx.onRequestImportApproval({
            sheetName: sheet.activeSheet, rowCount: sheet.rows.length, plan,
            limit: limit ?? undefined,
          })
          if (result === null) return fail("Import cancelled by reviewer.")
          finalApprovedKeys = result
        } else {
          const approved = await gate(
            `Import ${limit ?? sheet.rows.length} row(s) from ${sheet.activeSheet}`,
            `${plan.toCreate} service(s) created, ${plan.toUpdate} updated, ${plan.toSkip} skipped.\n` +
            `${approvedKeys.length} new supporting record(s) approved for creation.`,
          )
          if (!approved) return fail("Rejected by reviewer.")
          finalApprovedKeys = approvedKeys
        }

        return tryDo(() => {
          const result = applyImport(ctx.getStore(), sheet, m, {
            approvedNewValues: new Set(finalApprovedKeys),
            limit: limit ?? undefined,
          })
          commit(result.store)
          // One log line for the whole run rather than per-row entries: an
          // import can touch hundreds of entities, which would both blow past
          // the history cap and require snapshotting every touched entity
          // before applyImport's single-clone fast path mutates it in place.
          // Not individually restorable — see history.ts's makeBulkEntry.
          ctx.recordBulk?.(
            `Imported ${sheet.activeSheet}: ${result.created} created, ${result.updated} updated` +
            (result.skipped ? `, ${result.skipped} skipped` : "")
          )
          return ok({
            created: result.created,
            updated: result.updated,
            skipped: result.skipped,
            createdSupporting: result.createdSupporting,
            issues: result.issues.slice(0, 30),
            issueCount: result.issues.length,
            note:
              result.issues.length
                ? "Some rows imported with gaps — review the issues and fix those records individually."
                : "All rows imported cleanly.",
          })
        })
      },
    }),

    // ── Geocoding ─────────────────────────────────────────────────────────────────────────────────────────────
    svc_geocode: tool({
      description:
        "Convert a street address into coordinates using Photon, which searches OpenStreetMap data — the same data the " +
        "map renders. Use this to fill lat/lon AFTER you have verified an address from a real source; never guess " +
        "coordinates from memory. Returns ranked candidates with the address actually matched, so you can confirm it " +
        "resolved to the right building rather than to a city centre. If the match looks wrong or too coarse, leave the " +
        "coordinates empty and say so.",
      parameters: z.object({
        address: z.string().describe("The full street address, including city and country"),
        countryCode: z.string().nullable().describe("Two-letter country code to constrain the search, e.g. 'mx', or null"),
        limit: z.number().nullable().describe("Max candidates (default 3)"),
      }),
      execute: async ({ address, countryCode, limit }) => {
        try {
          const want = limit ?? 3
          // Photon has no country parameter, so filtering happens below; ask for
          // extra rows or the filter can discard the entire page of results.
          const params = new URLSearchParams({
            q: address,
            limit: String(countryCode ? Math.min(40, want * 8) : want),
          })
          // Photon is used rather than Nominatim directly because Nominatim
          // requires a User-Agent header, which browsers forbid scripts from
          // setting — every request from here would be rejected.
          const url = `https://photon.komoot.io/api/?${params}`

          const res = await fetch(url, { headers: { Accept: "application/json" } })
          if (res.status === 429) {
            return fail(
              "The geocoder is rate-limiting requests. Wait a moment and retry, and do not guess coordinates " +
              "in the meantime.",
            )
          }
          if (!res.ok) return fail(`Geocoding failed (HTTP ${res.status}).`)

          const body: any = await res.json()
          let features: any[] = Array.isArray(body?.features) ? body.features : []

          // Photon has no country filter, so narrow client-side when asked.
          if (countryCode) {
            const cc = countryCode.toUpperCase()
            features = features.filter(f => (f.properties?.countrycode ?? "").toUpperCase() === cc)
          }
          features = features.slice(0, want)

          if (!features.length) {
            return ok({
              address,
              found: 0,
              candidates: [],
              note: "No match. Do NOT invent coordinates — leave them empty and report that the address could not be geocoded.",
            })
          }

          // A result carrying a house number is a building; one that only has a
          // city or state is an area, and too coarse to pin a service to.
          const precisionOf = (pr: any) =>
            pr.housenumber ? "building"
              : pr.street ? "street"
              : pr.city || pr.district ? "area"
              : "region"

          return ok({
            address,
            found: features.length,
            candidates: features.map(f => {
              const pr = f.properties ?? {}
              const [lon, lat] = f.geometry?.coordinates ?? []
              return {
                lat: typeof lat === "number" ? +lat.toFixed(6) : null,
                lon: typeof lon === "number" ? +lon.toFixed(6) : null,
                matchedAddress: [pr.name, pr.housenumber, pr.street, pr.city, pr.state, pr.country]
                  .filter(Boolean).join(", "),
                precision: precisionOf(pr),
                countryCode: pr.countrycode,
              }
            }),
            note:
              "Check matchedAddress really is the place you meant. Only a precision of 'building' or 'street' is " +
              "specific enough for a service pin — 'area' or 'region' means only the surrounding place resolved, " +
              "so leave the coordinates empty instead.",
          })
        } catch (e: any) {
          return fail(`Geocoding failed: ${e?.message || String(e)}`)
        }
      },
    }),

    // ── Localization ────────────────────────────────────────────────────────────────────────
    svc_list_locales: tool({
      description:
        "List every configured locale with its translation coverage: how many records are translated, how many are " +
        "still missing, and how many are translated but not yet human-reviewed. Call this before starting localization work.",
      parameters: z.object({}),
      execute: async () => tryDo(() => {
        const s = ctx.getStore()
        return ok({
          defaultLocale: s.defaultLocale,
          activeLocale: L(),
          locales: Object.values(s.languages).map(l => ({
            code: l.code, name: l.name, direction: l.direction,
          })),
          coverage: db.localeCoverage(s),
        })
      }),
    }),

    svc_add_locale: tool({
      description:
        `Add a language to the Service Map so records can be translated into it. Only these codes are accepted, because they're the ones the Signpost sites can show: ${SITE_LOCALE_CODES.join(", ")}. ` +
        "Name and direction default from the code (Arabic, Farsi, Urdu and Pashto are right-to-left). Articles keeps its own list.",
      parameters: z.object({
        code: z.string().describe("BCP-47 locale code, e.g. 'es-MX'"),
        name: z.string().nullable().describe("The language's name in itself as readers see it (e.g. 'العربية'), or null to fill it in"),
        direction: z.enum(["ltr", "rtl"]).nullable().describe("Text direction, or null to take it from the code"),
      }),
      execute: async ({ code, name, direction }) => tryDo(() => {
        const r = normalizeLocale(code)
        if (r.error) return fail(r.error)
        const info = describeLanguage(r.code, { direction: direction ?? undefined })
        // The saved name is the one the public site shows readers, so it's in the language itself
        const saved = name?.trim() || nativeLanguageName(r.code)
        commit(db.upsertLanguage(ctx.getStore(), { code: info.code, name: saved, direction: info.direction }))
        return ok({ ...info, savedName: saved })
      }),
    }),

    svc_missing_translations: tool({
      description:
        "List records that have no translation for a locale, with the locale to translate FROM. " +
        "Use this to plan localization, then fill each gap with svc_translate_batch. " +
        "Narrow with `kinds` to work through one entity type at a time.",
      parameters: z.object({
        locale: z.string().describe("The locale to find gaps for, e.g. 'es-MX'"),
        kinds: z.array(z.enum(ENTITY_KINDS)).nullable().describe("Only these entity kinds, or null for all translatable ones"),
        limit: z.number().nullable().describe("Max rows (default 100)"),
      }),
      execute: async ({ locale, kinds, limit }) => tryDo(() => {
        const s = ctx.getStore()
        const missing = db.missingTranslations(s, locale, {
          kinds: kinds ?? undefined,
          limit: limit ?? 100,
        })
        return ok({
          locale,
          missingCount: missing.length,
          note: missing.length
            ? "Read each record with svc_get to see the source wording before translating."
            : `Nothing missing for ${locale}.`,
          missing,
        })
      }),
    }),

    svc_get_translations: tool({
      description:
        "Show every locale's wording for one record side by side, so you can compare against the source " +
        "and spot gaps, stale text, or unreviewed machine translation.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        id: z.string(),
      }),
      execute: async ({ kind, id }) => tryDo(() => {
        const s = ctx.getStore()
        const e = db.getEntity(s, kind, id)
        if (!e) return fail(`No such ${kind}: ${id}`)
        const configured = Object.keys(s.languages)
        const present = Object.keys(e.translations ?? {})
        return ok({
          kind, id,
          defaultLocale: s.defaultLocale,
          translations: e.translations ?? {},
          missingLocales: configured.filter(l => !present.includes(l)),
          unreviewedLocales: present.filter(l => !e.translations[l]?.reviewed),
          ...(kind === "service" ? { statusByLocale: localesByStatus(e) } : {}),
          lastEditedByLocale: Object.fromEntries(
            present.map(l => [l, e.translations[l]?.updatedAt
              ? new Date(e.translations[l].updatedAt).toISOString() : null]),
          ),
        })
      }),
    }),

    svc_translate_batch: tool({
      description:
        "Write one locale's translation for SEVERAL records in a single call — far more efficient than repeating " +
        "svc_set_translation. Read the source wording first (svc_get or svc_missing_translations). " +
        "Preserve HTML structure in descriptions and translate only human-readable text. " +
        "Translations land unreviewed unless you pass reviewed: true.",
      parameters: z.object({
        locale: z.string().describe("Target locale for every item below"),
        items: z.array(z.object({
          kind: z.enum(ENTITY_KINDS),
          id: z.string(),
          name: z.string().nullable().describe("Translated name, or null to leave"),
          description: z.string().nullable().describe("Translated HTML description, or null to leave"),
          address: z.string().nullable().describe("Translated address (services only), or null"),
          definition: z.string().nullable().describe("Translated definition (taxonomy only), or null"),
        })).describe("The records to translate"),
        reviewed: z.boolean().nullable().describe("Mark these as human-reviewed, or null to leave unreviewed"),
      }),
      execute: async ({ locale, items, reviewed }) => tryDo(() => {
        const storeAtStart = ctx.getStore()
        const written: any[] = []
        const failed: any[] = []

        // Validate and collect first, then apply the whole batch with ONE store
        // clone and ONE history flush. Writing per item cost a full-store deep
        // clone plus a full history serialize each time.
        const writes: db.TranslationWrite[] = []
        const fieldsFor = new Map<string, string[]>()
        for (const item of items) {
          if (!db.getEntity(storeAtStart, item.kind, item.id)) {
            failed.push({ kind: item.kind, id: item.id, error: "not found" })
            continue
          }
          // Nullable means "leave alone", so strip nulls before writing.
          const patch: Record<string, any> = {}
          for (const f of ["name", "description", "address", "definition"] as const) {
            if (item[f] !== null && item[f] !== undefined) patch[f] = item[f]
          }
          if (reviewed !== null && reviewed !== undefined) patch.reviewed = reviewed
          if (!Object.keys(patch).length) {
            failed.push({ kind: item.kind, id: item.id, error: "nothing to write" })
            continue
          }
          writes.push({ kind: item.kind, id: item.id, locale, patch })
          fieldsFor.set(`${item.kind}:${item.id}`, Object.keys(patch))
        }

        const entries: Parameters<NonNullable<typeof ctx.recordMany>>[0] = []
        const { store } = db.setTranslationsBulk(storeAtStart, writes, (w, beforeEntity) => {
          entries.push({
            action: "translate", actor: "agent",
            entityKind: w.kind, entityId: w.id,
            entityName: displayName(beforeEntity, locale, storeAtStart.defaultLocale),
            locale, summary: `Translated ${w.kind} (${locale}) via batch`,
            before: beforeEntity,
          })
          written.push({ kind: w.kind, id: w.id, fields: fieldsFor.get(`${w.kind}:${w.id}`) ?? [] })
        })

        // after-snapshots are read once the batch is settled.
        for (const e of entries) {
          const a = db.getEntity(store, e.entityKind, e.entityId)
          if (a) e.after = JSON.parse(JSON.stringify(a))
        }
        ctx.recordMany?.(entries)

        // One commit for the whole batch keeps React re-rendering once.
        commit(store)
        return ok({
          locale,
          writtenCount: written.length,
          failedCount: failed.length,
          written,
          ...(failed.length ? { failed } : {}),
        })
      }),
    }),

    svc_copy_locale: tool({
      description:
        "Seed one locale from another by copying the source wording verbatim into every record missing the target locale. " +
        "This does NOT translate — it creates unreviewed placeholders for a translator to work through. " +
        "Use it only when the user explicitly asks for placeholder rows; otherwise translate properly with svc_translate_batch.",
      parameters: z.object({
        fromLocale: z.string(),
        toLocale: z.string(),
        kinds: z.array(z.enum(ENTITY_KINDS)).nullable().describe("Only these kinds, or null for all translatable ones"),
      }),
      execute: async ({ fromLocale, toLocale, kinds }) => {
        const approved = await gate(
          `Seed ${toLocale} from ${fromLocale}`,
          `Copies untranslated source text into every record missing ${toLocale}. ` +
          `The copies are marked unreviewed and still need real translation.`,
        )
        if (!approved) return fail("Rejected by reviewer.")
        return tryDo(() => {
          const before = ctx.getStore()
          const gaps = db.missingTranslations(before, toLocale, { kinds: kinds ?? undefined, limit: 100000 })

          // Collect every write first, then apply them with ONE store clone and
          // ONE history flush. Doing it per gap cost a full-store deep clone and
          // a full history serialize each time — seconds of blocked main thread
          // on a few hundred records.
          const writes: db.TranslationWrite[] = []
          for (const g of gaps) {
            const src = db.getEntity(before, g.kind, g.id)?.translations?.[fromLocale]
            if (!src) continue
            writes.push({
              kind: g.kind, id: g.id, locale: toLocale,
              patch: {
                name: src.name,
                description: src.description,
                address: src.address,
                definition: src.definition,
                reviewed: false,
              },
            })
          }

          const summary = `Seeded ${toLocale} from ${fromLocale} (unreviewed placeholder)`
          const entries: Parameters<NonNullable<typeof ctx.recordMany>>[0] = []
          const { store, applied } = db.setTranslationsBulk(before, writes, (w, beforeEntity) => {
            entries.push({
              action: "translate", actor: "agent",
              entityKind: w.kind, entityId: w.id,
              entityName: displayName(beforeEntity, toLocale, before.defaultLocale),
              locale: toLocale, summary,
              before: beforeEntity,
            })
          })
          const copied = applied

          // after-snapshots are read once the batch is settled.
          for (const e of entries) {
            const a = db.getEntity(store, e.entityKind, e.entityId)
            if (a) e.after = JSON.parse(JSON.stringify(a))
          }
          ctx.recordMany?.(entries)
          commit(store)
          return ok({
            fromLocale, toLocale, copied,
            skipped: gaps.length - copied,
            note: `${copied} placeholder(s) created, all unreviewed and still needing real translation.`,
          })
        })
      },
    }),

    // ── Delete ──────────────────────────────────────────────────────────────
    svc_delete: tool({
      description:
        "Delete an entity. Anything referencing it is detached rather than deleted — scalar links are nulled and " +
        "id lists are pruned, matching the source schema's SET NULL behaviour. " +
        "Deleting a country detaches its regions, cities, providers and services, so state the scope before calling.",
      parameters: z.object({
        kind: z.enum(ENTITY_KINDS),
        id: z.string(),
      }),
      execute: async ({ kind, id }) => {
        const s0 = ctx.getStore()
        if (!db.getEntity(s0, kind, id)) return fail(`No such ${kind}: ${id}`)
        const refs = db.findReferences(s0, kind, id)
        const label = `${kind} "${name(kind, id)}"`
        const approved = await gate(
          `Delete ${label}`,
          refs.length
            ? `This will detach ${refs.length} reference(s):\n` +
              refs.slice(0, 20).map(r => `- ${r.kind}.${r.field}`).join("\n") +
              (refs.length > 20 ? `\n…and ${refs.length - 20} more` : "")
            : "Nothing references this record.",
        )
        if (!approved) return fail("Rejected by reviewer.")
        return tryDo(() => {
          const before = ctx.getStore()
          const { store, detached } = db.deleteEntity(before, kind, id)
          commitTrackedDelete({
            prevStore: before, nextStore: store,
            entityKind: kind, entityId: id,
            summary: `Deleted ${label}`,
          })
          ctx.onEntityDeleted?.(kind, id)
          return ok({ kind, id, detached })
        })
      },
    }),

    // ── Quality ─────────────────────────────────────────────────────────────
    svc_audit: tool({
      description:
        "Quality audit: services missing a provider, geography, categories or contact details; services with no " +
        "description; providers with no services; taxonomy entries nothing references; and per-locale translation coverage. " +
        "Use this to decide what to fix next.",
      parameters: z.object({
        countryId: z.string().nullable().describe("Limit to one country, or null for everything"),
        limit: z.number().nullable().describe("Max records listed per category (default 25). Counts are always exact — only the listings are capped."),
      }),
      execute: async ({ countryId, limit }) => tryDo(() => {
        const s = ctx.getStore()
        const dl = s.defaultLocale
        let services = Object.values(s.services)
        if (countryId) services = services.filter(x => x.countryId === countryId)

        const label = (x: any) => ({ id: x.id, name: displayName(x, L(), dl) })

        /**
         * Every category below could otherwise return the entire dataset, and
         * all of it lands in the model's context — roughly 25 tokens per row,
         * across seven lists, so a few hundred services can cost tens of
         * thousands of tokens per call. Report the exact count and a capped
         * sample instead; the count is what decides what to fix next, and the
         * agent can narrow with countryId or raise the limit deliberately.
         */
        const cap = limit ?? 25
        const listing = (rows: any[]) => {
          const items = rows.slice(0, cap).map(label)
          return rows.length > cap
            ? { count: rows.length, shown: items.length, items }
            : { count: rows.length, items }
        }
        const thin = (x: any) => {
          const t = x.translations?.[L()] ?? x.translations?.[dl]
          return db.stripHtml(t?.description ?? x.description).length < 40
        }

        const locales = Object.keys(s.languages)
        const coverage = locales.map(loc => {
          const total = services.length
          const done = services.filter(x => x.translations?.[loc]).length
          return { locale: loc, translated: done, total, missing: total - done }
        })

        return ok({
          scope: countryId ? name("country", countryId) : "all countries",
          serviceCount: services.length,
          missingProvider: listing(services.filter(x => !x.providerId)),
          missingGeography: listing(services.filter(x => !x.countryId || !x.regionId)),
          missingCategories: listing(services.filter(x => !x.categoryIds?.length)),
          missingContact: listing(services.filter(x => !x.contactInfo?.length)),
          thinDescription: listing(services.filter(thin)),
          drafts: listing(services.filter(x => statusFor(x, L()) === "draft")),
          // Oldest-touched first, so the agent can work through neglected records.
          stalest: [...services]
            .sort((a, b) => (a.updatedAt ?? 0) - (b.updatedAt ?? 0))
            .slice(0, 10)
            .map(x => ({ ...label(x), updatedAt: x.updatedAt ? new Date(x.updatedAt).toISOString() : null })),
          providersWithNoServices: listing(Object.values(s.providers)
            .filter(p => (!countryId || p.countryId === countryId) && db.servicesOfProvider(s, p.id).length === 0)),
          unusedCategories: listing(Object.values(s.serviceCategories)
            .filter(c => !services.some(x => x.categoryIds?.includes(c.id)))),
          unusedPopulations: listing(Object.values(s.populations)
            .filter(p => !services.some(x => x.populationIds?.includes(p.id)))),
          localeCoverage: coverage,
        })
      }),
    }),

    svc_schema_reference: tool({
      description:
        "The allowed values and required-field rules for this data model, taken from the source schema. " +
        "Call this when unsure what a field accepts — contact channels, hour days, statuses, or which FKs are mandatory.",
      parameters: z.object({}),
      execute: async () => ok({
        contactChannels: CONTACT_CHANNELS,
        hourDays: HOUR_DAYS,
        hourDaysNote:
          "These are the ONLY valid values for an opening-hours day. Note that the weekday block is " +
          "\"Monday to Friday \" with a trailing space — that is the stored value, not a typo. " +
          "Times are 24-hour \"HH:mm\".",
        serviceStatuses: SERVICE_STATUSES,
        providerStatuses: PROVIDER_STATUSES,
        categoryIcons: CATEGORY_ICONS,
        categoryIconsNote:
          "Service categories only. The public site draws a map pin ONLY for these exact names " +
          "and shows a blank pin for anything else, so a category with no icon — or an unrecognised " +
          "one — is invisible as a pin on the map. These are Material Symbol names, not Lucide names.",
        categoryColors: CATEGORY_COLORS,
        requiredFields: {
          region: ["countryId"],
          city: ["countryId", "regionId"],
          provider: ["countryId"],
          language: ["code"],
        },
        notes: [
          "Tags are a flat vocabulary with no parent — a service picks categories and tags independently.",
          "Publication status is per locale: each translation can be published or held back independently, " +
            "falling back to the service-wide status when the locale has none of its own.",
          "Cities require both country and region; the region must belong to that country.",
          "Service geography is country → region → city, each optional but progressively filtered.",
          "Services carry per-locale translations for name, description and address.",
          "Latitude/longitude are separate decimal fields; `point` mirrors the geometry column.",
          "A service category needs an `icon` to show a pin on the public map, and a `color` to be " +
            "distinguishable from other categories. Both are optional in the database and default to " +
            "null, so a newly created category is invisible on the map until an icon is set.",
        ],
      }),
    }),

    // ── History ──────────────────────────────────────────────────────────────
    // The context already carries getHistory/revert/versionsOf/
    // restoreEntityVersion; these expose them, matching cms_* and siteconfig_*.

    svc_history: tool({
      description: "List recent logged changes, newest last. Use to find an entry id for svc_revert_change, or to review what has changed recently.",
      parameters: z.object({ limit: z.number().nullable() }),
      execute: async ({ limit }) => tryDo(() => {
        const entries = ctx.getHistory?.() ?? []
        const slice = entries.slice(-(limit ?? 50))
        return ok({
          total: entries.length,
          entries: slice.map(e => ({
            id: e.id, at: new Date(e.at).toISOString(), action: e.action, actor: e.actor,
            entityKind: e.entityKind, entityId: e.entityId, entityName: e.entityName,
            summary: e.summary, reverted: !!e.reverted,
          })),
        })
      }),
    }),

    svc_entity_versions: tool({
      description: "The version timeline for one entity, newest first. Use before svc_restore_version to see what's available.",
      parameters: z.object({ entityId: z.string() }),
      execute: async ({ entityId }) => tryDo(() => {
        const versions = ctx.versionsOf?.(entityId) ?? []
        return ok({
          entityId,
          versions: versions.map(v => ({
            entryId: v.entryId, at: new Date(v.at).toISOString(), action: v.action,
            author: v.author, actor: v.actor, summary: v.summary,
            restorable: !!v.entity, isPriorState: !!v.isPriorState,
          })),
        })
      }),
    }),

    svc_restore_version: tool({
      description: "Restores an entity to a previously-recorded version. Logged as an update, so the restore itself is revertable. Always tell the user what you are restoring before calling this.",
      parameters: z.object({ kind: z.enum(ENTITY_KINDS), entityId: z.string(), entryId: z.string() }),
      execute: async ({ kind, entityId, entryId }) => {
        if (!ctx.restoreEntityVersion) return fail("Version restore is unavailable.")
        const approved = await gate(`Restore ${kind} "${name(kind, entityId)}" to an earlier version`)
        if (!approved) return fail("Rejected by reviewer.")
        const result = ctx.restoreEntityVersion(entityId, entryId, kind)
        if (result.ok === false) return fail(result.error)
        ctx.onEntityFocused?.(kind, entityId)
        return ok({ entityId, description: result.description })
      },
    }),

    svc_revert_change: tool({
      description: "Undo one logged change by its id, restoring the entity to how it was immediately before. Find ids with svc_history. Bulk import entries cover many rows at once and cannot be reverted this way.",
      parameters: z.object({ entryId: z.string() }),
      execute: async ({ entryId }) => {
        const entries = ctx.getHistory?.() ?? []
        const entry = entries.find(e => e.id === entryId)
        if (!entry) return fail(`No history entry with id ${entryId}`)
        if (!ctx.revert) return fail("Revert is unavailable.")
        const approved = await gate(`Undo: ${entry.summary}`)
        if (!approved) return fail("Rejected by reviewer.")
        const result = ctx.revert(entryId)
        if (result.ok === false) return fail(result.error)
        if (entry.entityKind && entry.entityId) ctx.onEntityFocused?.(entry.entityKind, entry.entityId)
        return ok({ entryId, reverted: true, description: result.description })
      },
    }),
  }
}

export const SERVICE_TOOL_LABELS: Record<string, string> = {
  svc_overview: "Service DB Overview",
  svc_search: "Search Services",
  svc_list: "List Entities",
  svc_get: "Get Entity",
  svc_create: "Create Entity",
  svc_update: "Update Entity",
  svc_set_translation: "Set Translation",
  svc_set_relations: "Set Relations",
  svc_set_status: "Set Status",
  svc_delete: "Delete Entity",
  svc_list_locales: "List Locales",
  svc_add_locale: "Add Locale",
  svc_missing_translations: "Find Missing Translations",
  svc_get_translations: "Compare Translations",
  svc_translate_batch: "Translate Records",
  svc_copy_locale: "Seed Locale",
  svc_audit: "Audit Services",
  svc_schema_reference: "Schema Reference",
  svc_geocode: "Geocode Address",
  svc_import_inspect: "Inspect Spreadsheet",
  svc_import_plan: "Plan Import",
  svc_import_apply: "Run Import",
  svc_history: "History",
  svc_entity_versions: "Entity Versions",
  svc_restore_version: "Restore Version",
  svc_revert_change: "Revert Change",
}
